'use client';

import { QueryKeys } from '@/constants/query-keys';
import { SharedState, SharedStateUpdate } from '@/types/shared-state';
import { commonFetch } from '@/utils/common-fetch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const defaultState: SharedState = {
  cartConcessionShowtimes: '',
  cartHasNonTicketItems: false,
  cartItemCount: 0,
  concessionsCheckoutUpsellShown: false,
  expressCheckoutMode: undefined,
  guestCustomerId: undefined,
  guestExpressPayEnabled: false,
  initialGeoAttempt: false,
  isAuthenticated: false,
  lastGeoPosition: undefined,
  lastTextSearch: undefined,
  lastViewedMarketUrl: undefined,
  lastViewedTheaterId: 0,
  lastViewedTheaterUrl: undefined,
  shoppingCartId: undefined,
  showMfaModal: null,
};

export const useSharedState = (): ReturnType<typeof useQuery<SharedState>> => {
  return useQuery<SharedState>({
    queryFn: async () => {
      const result = await commonFetch<SharedState>('/papi/shared-state');
      return result?.data;
    },
    queryKey: QueryKeys.user.sharedState,
  });
};

export const useUpdateSharedState = (updateCache = true): ReturnType<typeof useMutation<void, unknown, SharedStateUpdate>> => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, SharedStateUpdate>({
    mutationFn: async (partialState: SharedStateUpdate) => {
      await fetch('/papi/shared-state', {
        body: JSON.stringify(partialState),
        method: 'PUT',
      });
    },
    onSuccess: (_, partialState) => {
      if (!updateCache) return;

      queryClient.setQueryData(QueryKeys.user.sharedState, (oldData: SharedState | undefined) => {
        if (!oldData) {
          return {
            ...defaultState,
            ...partialState,
          };
        }

        return {
          ...oldData,
          ...partialState,
        };
      });
    },
  });
};

/**
 * Fetches the latest shared state from the server.
 * Note: This function is exposed via authentication and may be called by consumers as needed.
 * prefer using the local shared state when possible, and call this function only when you need to ensure you have the most up-to-date state from the server.
 * This does not update any local state or cache; it simply fetches the latest state from the server.
 */
export const refreshSharedState = async (): Promise<SharedState> => {
  try {
    const result = await commonFetch<SharedState>('/papi/shared-state');
    return result?.data || defaultState;
  } catch {
    return defaultState;
  }
};
